import { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Badge, type BadgeProps, Button, Tabs, ButtonLink, TextLink } from '@/shared/components';
import { PermissionGuard } from '@/features/auth';
import { usePlanQuery } from '../hooks/usePlanQuery';
import { usePlanMutations } from '../hooks/usePlanMutations';
import { PlanProductPanel } from '../components/PlanProductPanel';
import { PlanServicePanel } from '../components/PlanServicePanel';
import { PlanVehicleRulePanel } from '../components/PlanVehicleRulePanel';

type Tab = 'details' | 'products' | 'services' | 'vehicle-rules';

const statusBadgeVariant = (status: string): BadgeProps['variant'] =>
    status === 'active' ? 'success' : status === 'retired' ? 'error' : 'outline';

const TABS: { id: Tab; label: string }[] = [
    { id: 'details', label: 'Details' },
    { id: 'services', label: 'Services' },
    { id: 'vehicle-rules', label: 'Vehicle Rules' },
    { id: 'products', label: 'Products' },
];

export function PlanDetail() {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Origin-aware back breadcrumb: honour the entry point passed via router
    // state (e.g. from a product's Plans tab), defaulting to the Plans list.
    const navState = location.state as { backTo?: string; backLabel?: string } | null;
    const backTo = navState?.backTo ?? '/smr-admin/plans';
    const backLabel = navState?.backLabel ?? 'Back to Plans';
    const initialTab = (searchParams.get('tab') as Tab | null) ?? 'details';
    const [activeTab, setActiveTab] = useState<Tab>(TABS.some((t) => t.id === initialTab) ? initialTab : 'details');

    const query = usePlanQuery(id);
    const { deleteMutation, retireMutation } = usePlanMutations();

    if (!id) return <p className="text-mp-error">Invalid plan ID.</p>;
    if (query.isLoading) return <p>Loading plan...</p>;
    if (query.isError || !query.data) {
        return <Alert variant="error"><span>Plan not found or failed to load.</span></Alert>;
    }

    const plan = query.data;

    const handleDelete = () => {
        deleteMutation.mutate(id, {
            onSuccess: () => navigate('/smr-admin/plans'),
        });
    };

    const handleRetire = () => {
        retireMutation.mutate(id, {
            onSuccess: () => navigate('/smr-admin/plans'),
        });
    };

    return (
        <section className="space-y-4">
            <div className="flex items-center gap-2">
                <TextLink to={backTo} color="secondary" className="text-sm">&larr; {backLabel}</TextLink>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-semibold">{plan.name}</h1>
                    <Badge variant={statusBadgeVariant(plan.status)}>{plan.status}</Badge>
                </div>
                <PermissionGuard permission={{ resource: 'smr', action: 'plan.edit' }}>
                    <ButtonLink to={`/smr-admin/plans/${id}/edit`} variant="outline" size="xs">
                        Edit Plan
                    </ButtonLink>
                </PermissionGuard>
            </div>

            <Tabs
                tabs={TABS}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                idPrefix="plan-detail"
                ariaLabel="Plan sections"
            >
                {activeTab === 'details' && (
                    <div className="space-y-6 max-w-2xl">
                        <h2 className="text-base font-medium text-mp-foreground">
                            Key details for this Plan
                        </h2>
                        <table className="w-full text-sm">
                            <tbody>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left w-40">ID</th>
                                    <td className="px-3 py-2 text-left font-mono text-xs">{plan.id}</td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Name</th>
                                    <td className="px-3 py-2 text-left font-semibold">{plan.name}</td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Description</th>
                                    <td className="px-3 py-2 text-left">{plan.description || <span className="text-mp-muted">—</span>}</td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Status</th>
                                    <td className="px-3 py-2 text-left"><Badge variant={statusBadgeVariant(plan.status)}>{plan.status}</Badge></td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Schedule</th>
                                    <td className="px-3 py-2 text-left">{plan.is_looped ? 'Looped: repeats after the last service' : 'Finite: ends after the last service'}</td>
                                </tr>
                                {plan.retired_at && (
                                    <tr className="border-b border-mp-border">
                                        <th className="px-3 py-2 text-left">Retired At</th>
                                        <td className="px-3 py-2 text-left">{new Date(plan.retired_at).toLocaleString()}</td>
                                    </tr>
                                )}
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Created</th>
                                    <td className="px-3 py-2 text-left">{new Date(plan.created_at).toLocaleString()}</td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Updated</th>
                                    <td className="px-3 py-2 text-left">{new Date(plan.updated_at).toLocaleString()}</td>
                                </tr>
                            </tbody>
                        </table>

                        <PermissionGuard permission={{ resource: 'smr', action: 'plan.delete' }}>
                            <div className="border-t border-mp-border pt-6 space-y-4">
                                <div>
                                    <h3 className="font-semibold mb-2">Retire Plan</h3>
                                    <div className="flex items-start gap-4 justify-between">
                                        <p className="text-sm text-mp-muted">
                                            Retiring a plan marks it inactive. Services and products remain linked.
                                        </p>
                                        <Button
                                            size="sm"
                                            variant="warning"
                                            type="button"
                                            onClick={handleRetire}
                                            disabled={retireMutation.isPending || plan.status === 'retired'}
                                            className="whitespace-nowrap"
                                        >
                                            {retireMutation.isPending ? 'Retiring...' : 'Retire Plan'}
                                        </Button>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="font-semibold mb-2">Delete Plan</h3>
                                    <div className="flex items-start gap-4 justify-between">
                                        <p className="text-sm text-mp-muted">
                                            Deleting a plan removes it permanently along with all product and service assignments.
                                        </p>
                                        <Button
                                            size="sm"
                                            variant="danger"
                                            type="button"
                                            onClick={handleDelete}
                                            disabled={deleteMutation.isPending}
                                            className="whitespace-nowrap"
                                        >
                                            {deleteMutation.isPending ? 'Deleting...' : 'Delete Plan'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </PermissionGuard>
                    </div>
                )}

                {activeTab === 'products' && (
                    <PermissionGuard permission={{ resource: 'smr', action: 'plan.read' }}>
                        <h2 className="mb-4 text-base font-medium text-mp-foreground">
                            These are the Products this Plan is assigned to
                        </h2>
                        <PlanProductPanel planId={id} />
                    </PermissionGuard>
                )}

                {activeTab === 'services' && (
                    <PermissionGuard permission={{ resource: 'smr', action: 'plan.read' }}>
                        <h2 className="mb-4 text-base font-medium text-mp-foreground">
                            These are the Services included in this Plan
                        </h2>
                        <PlanServicePanel planId={id} planName={plan.name} />
                    </PermissionGuard>
                )}

                {activeTab === 'vehicle-rules' && (
                    <PermissionGuard permission={{ resource: 'smr', action: 'plan.read' }}>
                        <h2 className="mb-4 text-base font-medium text-mp-foreground">
                            These are the Vehicle Rules that apply to this Plan
                        </h2>
                        <PlanVehicleRulePanel planId={id} />
                    </PermissionGuard>
                )}
            </Tabs>
        </section>
    );
}
